import React, { useState, useMemo } from 'react';
import { FORMS } from '../../schemas/stepMap';
import FormBuilder from '../../components/formBuilder';
import { Helmet } from 'react-helmet';
import { useProperty } from '../../hooks/useProperty';
import SummaryProjection from './SummaryProjection';
import BackForwardButtons from '../../components/buttons/BackForwardButtons';
import { useSteps } from '../../hooks/useSteps';
import { projectionCalculator } from './Comparison/projectionCalculator';
import graphDataFormatter from './Comparison/graphDataFormatter';
import InvestmentMetrics from '../../components/InvestmentMetrics';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../hooks/useUser';

const graphSelectOptions = [
  {
    name: 'monthlyNOI',
    description: 'Monthly Income After Operating Expenses',
    label: 'Net Income',
  },
  {
    name: 'totalEquity',
    description:
      'Total Equity Based on the Estimated Market Value of the Property on Purchase',
    label: 'Total Equity',
  },
  {
    name: 'annualPreTaxCashFlow',
    description: 'Annual Pre-Tax Cash Flow',
    label: 'Cash Flow',
  },
  {
    name: 'totalRoiYear1Amount',
    description: 'Return On Investment',
    label: 'ROI',
  },
  {
    name: 'marketPurchasePrice',
    description:
      'Property Value Based on the estimated property value on purchase (not purchase price)',
    label: 'Property Value',
  },
  {
    type: 'stack',
    stackIncludes: [
      { name: 'yearOnePrincipalReduction', label: 'Principal' },
      {
        name: 'interest',
        label: 'Interest',
        derivedFrom: [
          'monthlyDebtService',
          'balloonPayment',
          'yearOnePrincipalReduction',
        ],
        calcFunction: (
          monthlyDebtService,
          balloonPayment,
          yearOnePrincipalReduction
        ) =>
          monthlyDebtService +
          (balloonPayment || 0) -
          yearOnePrincipalReduction,
      },
    ],
    description: 'Mortgage Payments over the years',
    label: 'Amortization',
  },
];

export default function PurchaseSummary() {
  const { propertyData } = useProperty();
  const { prev, curr, next } = useSteps();
  const purchaseWorksheet = curr || FORMS.purchaseAnalysis;
  const purchaseWorksheetSchema = purchaseWorksheet.schema;
  const { userDocData, user } = useUser();

  const { pathname } = useLocation();
  const sharing = pathname.includes('sharing');

  if (!propertyData.purchaseWorksheetSubmitted) {
    return (
      <div className="w-full flex flex-col items-center justify-center mt-36">
        <h2 className="mb-6 text-xl font-medium text-primary">
          No Purchase Analysis Created
        </h2>
        {!sharing && (
          <p className="mb-72 w-2/3 text-center">
            Once you add property details in the{' '}
            <Link
              to="../../purchase-worksheet"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              purchase worksheet
            </Link>
            , you will be able to see your property analysis here.
          </p>
        )}
      </div>
    );
  }

  const [graph, setGraph] = useState(0);
  const [years, setYears] = useState(30);

  const projectionForm = FORMS.projection;

  const purchaseSchema = projectionForm.schema.calculations.schema[0];
  const purchaseFields = purchaseSchema.schema;

  const projectionData = useMemo(
    () => projectionCalculator(years, purchaseFields, propertyData),
    [years, propertyData]
  );

  const graphData = graphDataFormatter(
    years,
    projectionData,
    {},
    {},
    graphSelectOptions[graph]?.name,
    null,
    null,
    'single',
    graphSelectOptions[graph]
  );

  return (
    <>
      <Helmet>
        <title>PropertyCast.io - Purchase Analysis</title>
      </Helmet>
      <div>
        {user && !userDocData.loading && (
          <InvestmentMetrics
            schema={purchaseWorksheet.investmentMetrics}
            data={{ ...userDocData, ...propertyData }}
          />
        )}
      </div>
      <div className="pt-4 pb-2">
        <FormBuilder
          formType={purchaseWorksheet.formType}
          calculationsSchema={purchaseWorksheetSchema}
          beforeData={propertyData}
          keyMetricsSchema={purchaseWorksheet.keyMetrics}
          keyMetricsData={propertyData}
        />
        <div>
          <SummaryProjection
            options={graphSelectOptions}
            currOption={graph}
            setOption={setGraph}
            years={years}
            setYears={setYears}
            graphData={graphData}
            tableData={projectionData}
            schema={purchaseSchema}
          />
        </div>
        <BackForwardButtons prev={prev} next={next} />
      </div>
    </>
  );
}
